import { Component, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, combineLatest, merge } from 'rxjs';
import { map, scan } from 'rxjs/operators';
import { WorkService } from '../services';
import { CoreService } from '@app/core/services';
import { TeleworkerState } from '@app/core/models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CallbackService } from '../services/callback.service';
import { DiallerService } from '@app/dialler/services';
import { ToastrService } from 'ngx-toastr';
import { HubConnectionState } from '@microsoft/signalr';
import { LoggerService } from '@adreno-common';
@Component({
  selector: 'app-management-panel',
  templateUrl: './management-panel.component.html',
  styles: [
    '.btn-sm { line-height: 14px; border-radius: 4px; }',
    'small { font-size: 12px; font-weight: 700; }',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManagementPanelComponent implements OnInit {
  private availableBtn$!: Observable<ButtonState>;
  private exitBtn$!: Observable<ButtonState>;
  private msgBtn$!: Observable<ButtonState>;
  private tasksBtn$!: Observable<ButtonState>;

  viewModel$!: Observable<ViewModel>;

  constructor(
    private router: Router,
    private workSvc: WorkService,
    private coreSvc: CoreService,
    private callbackSvc: CallbackService,
    private diallerSvc: DiallerService,
    private toastrSvc: ToastrService,
    private logger: LoggerService
  ) {}

  private initButtonStates() {
    this.availableBtn$ = combineLatest([
      this.diallerSvc.state$,
      this.diallerSvc.onVoiceMailCall$,
      this.workSvc.hasIBScheduled$,
      this.workSvc.obLaunched$,
      this.workSvc.campaignScriptOpen$,
      combineLatest([
        this.workSvc.campaignId$,
        this.diallerSvc.connectedState$,
      ]),
    ]).pipe(
      map(
        ([
          twState,
          onVoicemailCall,
          hasIBScheduled,
          obLaunched,
          campaignScriptOpen,
          [campaignId, connected],
        ]) => {
          switch (twState) {
            case TeleworkerState.AvailableIB:
            case TeleworkerState.CallDialling:
            case TeleworkerState.WrapIB:
            case TeleworkerState.InboundConnected:
            case TeleworkerState.InboundWaiting:
            case TeleworkerState.Standby:
            case TeleworkerState.SIPNailIn:
            case TeleworkerState.Standby:
            case TeleworkerState.OutboundConnected:
            case TeleworkerState.HoldIB:
            case TeleworkerState.HoldOB:
            case TeleworkerState.TransferIB:
            case TeleworkerState.TransferOB:
            case TeleworkerState.Ringing:
              return {
                visible: hasIBScheduled,
                hoverText:
                  connected === HubConnectionState.Connected
                    ? ''
                    : 'You are currently not connected',
                enabled: false,
              };
            case TeleworkerState.CallDisconnected:
            case TeleworkerState.WrapOB:
              return {
                visible: hasIBScheduled,
                hoverText:
                  connected === HubConnectionState.Connected
                    ? ''
                    : 'You are currently not connected',
                enabled:
                  connected === HubConnectionState.Connected &&
                  !campaignScriptOpen &&
                  !onVoicemailCall,
              };
            case TeleworkerState.Voicemail:
              return {
                visible: true,
                hoverText:
                  connected === HubConnectionState.Connected
                    ? ''
                    : 'You are currently not connected',
                enabled: false,
              };
            default:
              return {
                visible: hasIBScheduled,
                hoverText:
                  connected === HubConnectionState.Connected
                    ? ''
                    : 'You are currently not connected',

                enabled:
                  (campaignId !== undefined &&
                  campaignId !== 0 &&
                  !obLaunched &&
                  campaignScriptOpen
                    ? false
                    : !obLaunched) &&
                  connected === HubConnectionState.Connected &&
                  !onVoicemailCall,
              };
          }
        }
      )
    );

    this.msgBtn$ = combineLatest([
      this.workSvc.hasNNRecords$,
      this.diallerSvc.connectedState$,
    ]).pipe(
      map(([hasNN, connected]) => ({
        visible: true,
        hoverText:
          connected === HubConnectionState.Connected
            ? ''
            : 'You are currently not connected',

        enabled: (connected === HubConnectionState.Connected && hasNN) || false,
      }))
    );

    this.tasksBtn$ = combineLatest([
      this.callbackSvc.userCallbacks$,
      this.coreSvc.state$,
      this.diallerSvc.connectedState$,
    ]).pipe(
      map(([callbacks, state, connected]) => ({
        visible: true,
        hoverText:
          connected === HubConnectionState.Connected
            ? ''
            : 'You are currently not connected',
        enabled:
          (state &&
            [
              TeleworkerState.Work,
              TeleworkerState.Unavailable,
              TeleworkerState.UnavailableIB,
              TeleworkerState.UnavailableOB,
              TeleworkerState.DiallerLogout,
            ].includes(state) &&
            callbacks &&
            callbacks.length > 0 &&
            connected === HubConnectionState.Connected) ||
          false,
      }))
    );

    this.exitBtn$ = combineLatest([
      this.workSvc.master$,
      this.workSvc.obLaunched$,
      this.coreSvc.state$,
    ]).pipe(
      map(([master, oblaunched, corestate]) => ({
        visible: true,
        hoverText: '',
        enabled:
          corestate === TeleworkerState.Work
            ? true
            : oblaunched === true
            ? false
            : master === undefined,
      }))
    );
  }

  private initViewModel() {
    this.viewModel$ = merge(
      this.availableBtn$.pipe(map((btnState) => ({ available: btnState }))),
      this.msgBtn$.pipe(map((msg) => ({ msg }))),
      this.exitBtn$.pipe(map((exit) => ({ exit }))),
      this.tasksBtn$.pipe(map((tasks) => ({ tasks })))
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  available() {
    this.logger.info('ManagementPanelComponent', 'available()')();
    this.diallerSvc.availableib();
  }

  exit() {
    this.logger.info('ManagementPanelComponent', 'exit()')();
    if (this.router.url.includes('nameandnumber')) {
      this.toastrSvc.info('Please handle currently loaded msg before exiting');
    } else {
      this.diallerSvc.ibxferonly = false;
      this.workSvc.exit();
      this.workSvc.unsetWork();
      this.workSvc.routeToNews();
    }
  }

  ngOnInit(): void {
    this.initButtonStates();
    this.initViewModel();
  }

  openTasks() {
    this.logger.info('ManagementPanelComponent', 'openTasks()')();
    this.workSvc.openTasksModal();
  }

  showVoicemailModal() {
    this.logger.info('ManagementPanelComponent', 'showVoicemailModal()')();
    this.workSvc.openVoicemailMsg();
  }
}

interface ViewModel {
  available: ButtonState;
  exit?: ButtonState;
  msg?: ButtonState;
  state?: boolean;
  tasks?: ButtonState;
}

interface ButtonState {
  enabled: boolean;
  hoverText: string;
  visible: boolean;
}

/*
    of({ msg: false, tasks: false, exit: true }),
    combineLatest([
      this.workSvc.hasIBScheduled$,
      this.workSvc.obLaunched$,
      this.coreSvc.state$,
      this.workSvc.ibLaunched$,
      this.workSvc.campaignId$,
      this.workSvc.hasNNRecords$,
    ]).pipe(
      map(
        ([
          hasIBScheduled,
          obLaunched,
          state,
          ibLaunched,
          campaignId,
          nnrecords,
        ]) => {
          let vm: Partial<ViewModel> = {
            msg: nnrecords,
          };

          switch (state) {
            case TeleworkerState.HoldIB:
            case TeleworkerState.HoldOB:
            case TeleworkerState.TransferIB:
            case TeleworkerState.TransferOB:
              vm = {
                ...vm,
                msg: false,
              };
              break;
            case TeleworkerState.Voicemail:
              this.onVoicemailCall = true;
              break;
            case TeleworkerState.Work:
              this.onVoicemailCall = false;
              vm = {
                ...vm,
                available: { visible: hasIBScheduled, enabled: !obLaunched },
              };
              break;
            case TeleworkerState.WrapIB:
              vm = {
                ...vm,
                available: { visible: hasIBScheduled, enabled: false },
              };
              break;
            //case TeleworkerState.Work:
            //  this.onVoicemailCall = false;
            //  vm = {
            //    ...vm,
            //    available: { visible: true, enabled: !obLaunched },
            //  };
            //  break;

            case TeleworkerState.DiallerLoggedIn:
              vm = {
                ...vm,
                available: { visible: hasIBScheduled, enabled: !obLaunched },
              };
              break;
            case TeleworkerState.Unavailable:
            case TeleworkerState.UnavailableIB:
            case TeleworkerState.UnavailableOB:
              this.onVoicemailCall = false;
              vm = {
                ...vm,
                available: { visible: hasIBScheduled, enabled: !obLaunched },
              };
              break;
            case TeleworkerState.IBMissedCall:
              vm = {
                ...vm,
                available: { visible: hasIBScheduled, enabled: !obLaunched },
              };
              break;

            case TeleworkerState.AvailableIB:
            case TeleworkerState.CallDialling:
              vm = {
                ...vm,
                available: { visible: hasIBScheduled, enabled: false },
              };
              break;
            case TeleworkerState.WrapOB:
              if (!ibLaunched && !obLaunched) {
                vm = {
                  ...vm,
                  available: { visible: hasIBScheduled, enabled: true },
                };
              } else {
                vm = {
                  ...vm,
                  available: { visible: hasIBScheduled, enabled: false },
                };
              }
              break;
          }

          if (
            campaignId !== undefined &&
            campaignId !== 0 &&
            !obLaunched &&
            ibLaunched
          ) {
            // we have an ib campaign selected so want unavailable and available to be not enabled
            vm = {
              ...vm,
              available: {
                visible:
                  vm.available?.visible === undefined
                    ? false
                    : vm.available?.visible,
                enabled: false,
              },
            };
          }

          if (this.onVoicemailCall) {
            vm = {
              ...vm,
              available: { visible: hasIBScheduled, enabled: false },
            };
          }

          return vm;
        }
      )
    )*/
